import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOptionalData } from '../../contexts/DataContext';
import useAppChrome from '../../hooks/useAppChrome';
import TopBar from './TopBar';
import MyPreferencesDialog from './MyPreferencesDialog';
import { confirmLeave, hasLeaveGuard } from '../../utils/leaveGuard';

const currentPlace = (pathname) => {
    if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'admin';
    if (pathname === '/branches') return 'branches';
    if (pathname === '/tasks') return 'tasks';
    if (pathname === '/') return 'inventory';
    return null;
};

/**
 * The v2 top bar wired to the app (shown only with the new design flag on,
 * see App.js). Every behaviour comes from today's headers via useAppChrome:
 *
 * - dealership in view (workspace name over /me org), Read-only badge,
 *   "Switch dealership" only for multi-dealership users (same picker flow);
 * - "Updated Xm ago", amber from 2 h; "Last updated" in the user menu;
 * - Admin only for admins; Refresh = the forced inventory reload, on the
 *   inventory page where it exists today;
 * - user menu with name / email, My preferences (starting layout and
 *   personal margin floor) and Sign out (logout).
 *
 * In the admin console it stands in for the console's own top bar: the scope
 * ("Fusable Admin" / "Team Admin") and the admin's org, and the nav links ask
 * which dealership first, exactly like "IronSight view →" did (D18).
 */
function AppTopBar({ me, user, onLogout }) {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const data = useOptionalData();
    const chrome = useAppChrome({ orgName: me?.org_name, data });
    const current = currentPlace(pathname);
    const inConsole = current === 'admin';

    // Leaving the console: settle on a dealership first; backing out of the
    // picker leaves the admin where they are.
    const leaveConsole = async (e, to) => {
        e.preventDefault();
        // Unsaved dealership settings ask first, in the page (QA P2-6).
        if (hasLeaveGuard() && !(await confirmLeave())) return;
        const chosen = await chrome.requestWorkspace();
        if (chosen) navigate(to);
    };

    // The IronSight name (/me) first, as v1's headers do; the MSAL account name as fallback (QA A-6).
    const name = me?.display_name || user?.name || user?.username || 'User';
    const email = user?.username || me?.email;
    const [prefsOpen, setPrefsOpen] = useState(false);

    return (
        <>
        <TopBar
            dealership={inConsole ? me?.org_name : chrome.dealership}
            dealershipTitle={inConsole ? 'Organization currently in view' : 'Dealership currently in view'}
            scopeLabel={inConsole ? (me?.is_super_admin ? 'Fusable Admin' : 'Team Admin') : undefined}
            readOnly={!inConsole && chrome.readOnly}
            canSwitch={!inConsole && chrome.canSwitch}
            onSwitch={chrome.switchDealership}
            cacheStatus={chrome.cacheStatus}
            current={current}
            onNavigate={inConsole ? leaveConsole : undefined}
            isAdmin={Boolean(me?.is_admin)}
            showRefresh={current === 'inventory'}
            onRefresh={chrome.refresh}
            refreshing={chrome.refreshing}
            userName={name}
            userEmail={email}
            onSignOut={onLogout}
            onPreferences={() => setPrefsOpen(true)}
        />
        {/* My preferences (docs/decisions/role-presets.md): starting layout and margin floor. */}
        <MyPreferencesDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} />
        </>
    );
}

AppTopBar.propTypes = {
    me: PropTypes.shape({
        is_admin: PropTypes.bool,
        is_super_admin: PropTypes.bool,
        org_name: PropTypes.string,
        display_name: PropTypes.string,
        email: PropTypes.string,
    }),
    user: PropTypes.shape({ name: PropTypes.string, username: PropTypes.string }),
    onLogout: PropTypes.func.isRequired,
};

export default AppTopBar;
